import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Inject,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
    FormsModule,
    ReactiveFormsModule,
    FormGroup,
    FormBuilder,
    Validators,
    FormControl,
    FormArray,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
    MatDialogModule,
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialog,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';
import { SaiQuickTourService } from 'app/ui/sai-quick-tour/sai-quick-tour.service';

import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subject, catchError, finalize, of, takeUntil } from 'rxjs';

import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { DataSourceService } from '../../dataSources.service';
import { DepartmentService } from 'app/company/department/department.service';
import { Department } from 'app/company/department/department';
import {
    MatSelectSearchComponent,
    NgxMatSelectSearchModule,
} from 'ngx-mat-select-search';
import { DisableWhileLoadingDirective } from 'app/modules/shared/button-loading.directive';
import { AddExternalTableComponent } from '../add-external-table/add-external-table.component';
import { DeleteConfirmationComponent } from 'app/ui/delete-confirmation/delete-confirmation.component';
import { environment } from 'environments/environment';
import { DataSourceFormService } from '../../data-source-form.service';
import {
    dataSourceTypeChangeLocks,
    DataSourceContentCounts,
    DataSourceTypeChangeLocks,
} from '../../data-source-type-change.util';
import { UserService } from 'app/core/user/user.service';
import { OurAuthService } from 'app/core/auth/auth.service';

/** Prefill used when the SAI Quick Tour opens this dialog on step 7. */
const TOUR_DEMO_DS_NAME = 'Demo Sales Data';
const TOUR_DEMO_DS_TYPE = 'internal';
const TOUR_DEMO_DS_INTERNAL_TYPE = 'Structured Tables';

@Component({
    selector: 'app-add-data-sources',
    standalone: true,
    imports: [
        CommonModule,
        CdkScrollable,
        TranslocoModule,
        FormsModule,
        MatDialogModule,
        MatInputModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatOptionModule,
        MatSlideToggleModule,
        NgxMatSelectSearchModule,
        DisableWhileLoadingDirective,
        MatProgressSpinnerModule,
        MatTooltipModule,
        SaiDialogHeaderComponent,
    ],
    templateUrl: './add-data-sources.component.html',
    styleUrl: './add-data-sources.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MatSelectSearchComponent),
            multi: true,
        },
    ],
})
export class AddDataSourcesComponent implements OnInit, OnDestroy {
    form: FormGroup;
    protected _unsubscribeAll = new Subject<void>();
    lang: string = 'en';
    departmenList: Department[] = [];
    membersList: any[] = [];
    isAdmin: boolean = false;

    tables = [
        {
            nameLevel1: 'Custom Views',
            valuesLevel1: [
                {
                    value: 'ZSQL_MM_MAT_EXP',
                    viewValue: 'ZSQL_MM_MAT_EXP- Product Expiration',
                },
                {
                    value: 'ZCDS_MC4_SALES',
                    viewValue: 'ZCDS_MC4_SALES- Universal Sales Data',
                },
            ],
        },
        {
            nameLevel1: 'Tables',
            valuesLevel1: [
                {
                    value: 'ZCDS_MC4_SALES',
                    viewValue: 'ZCDS_MC4_SALES- Universal Sales Data',
                },
            ],
        },
    ];

    tableFilterCtrl = new FormControl();
    filteredTables: any[] = [];
    showSpinner: boolean = false;
    /** Overlay while Edit/View dialog hydrates departments and members. */
    isLoadingDataSourceData = false;
    /** Department ids this data source is shared with (synced via share-departments). */
    sharedDepartmentIds: number[] = [];
    shareSaving = false;
    private _isHydratingEditView = false;
    private _hydratePending = 0;
    /** Department ids at Edit open — used to detect department changes on save. */
    private _initialDepartmentIds: number[] = [];
    /** Prefill + start overlay coach when tour is on Create a data source. */
    private _tourPrefillApplied = false;
    private readonly _quickTour = inject(SaiQuickTourService);
    private readonly _userService = inject(UserService);
    private readonly _authService = inject(OurAuthService);
    /** Cached signed-in user (login / profile) for Add Owned By default. */
    private _currentUser: Record<string, unknown> | null = null;
    /** Stable signed-in id (survives profile payloads that omit `id`). */
    private _loggedInUserId: number | null = null;
    /** Stable signed-in email for matching org-users rows. */
    private _loggedInUserEmail: string = '';
    /** Display name for the Owned By default (Add). */
    private _loggedInDisplayName: string = '';
    /** Authoritative table/upload/media counts for Edit type-change locks. */
    private _contentCounts: DataSourceContentCounts = {};

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly _dialogRef: MatDialogRef<AddDataSourcesComponent>,
        private readonly _dialog: MatDialog,
        private readonly _alertService: CustomAlertService,
        private readonly _departmentService: DepartmentService,
        private readonly _dataSourceService: DataSourceService,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _translocoService: TranslocoService,
        private readonly _httpClient: HttpClient,
        private readonly _formService: DataSourceFormService
    ) { }

    ngOnInit(): void {
        this._initAdminFlag();
        this._subscribeToLangChanges();
        this._initForm();
        this._subscribeToLoggedInUser();
        this._maybeStartTourFormCoach();

        const hydrate =
            this.data?.type === 'Edit' || this.data?.type === 'View';
        if (hydrate) {
            this._isHydratingEditView = true;
            this.isLoadingDataSourceData = true;
            this.beginHydrationTask();
            this.beginHydrationTask();
            this.beginHydrationTask();
        }

        this._loadDepartments(hydrate);
        this._subscribeToDepartmentChanges();
        this._loadMembers(
            this._formService.normalizeDepartmentIds(
                this.form.get('department')?.value,
            ),
            hydrate,
        );
        this._loadContentCountsForTypeLocks(hydrate);
    }

    ngOnDestroy(): void {
        if (this.data?.type === 'Add') {
            this._quickTour.completeAddDialogStep('add-data-source');
        } else {
            this._quickTour.endFormCoach();
        }
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private _initAdminFlag(): void {
        this.isAdmin = this._authService.userRole === 'company';
    }

    private _subscribeToLangChanges(): void {
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                this.lang = res;
                this._changeDetectorRef.markForCheck();
            });
    }

    private _initForm(): void {
        this.form = this._formService.createEmptyForm();

        if (this.data?.dataSource) {
            this._formService.patchFromDataSource(
                this.form,
                this.data.dataSource,
                this.isAdmin,
                this.data.type
            );
            this.sharedDepartmentIds = this._formService
                .sharedDepartmentIdsFromDataSource(this.data.dataSource)
                .slice();
            this._initialDepartmentIds = this._formService
                .normalizeDepartmentIds(this.form.get('department')?.value)
                .slice()
                .sort((a, b) => a - b);
        } else if (this.data?.type === 'Add') {
            // New data sources share with all assigned departments by default;
            // the picker lets the creator opt departments in or out. Applied in save().
            this.sharedDepartmentIds = this._formService
                .normalizeDepartmentIds(this.form.get('department')?.value)
                .slice();
        }

        if (this.data?.type === 'Add' || this.data?.type === 'Edit') {
            const managerCtrl = this.form.get('manager');
            managerCtrl?.setValidators([Validators.required]);
            managerCtrl?.updateValueAndValidity({ emitEvent: false });
        }

        if (this.data?.type === 'Add' || this.data?.type === 'View') {
            this.form.get('manager')?.disable({ emitEvent: false });
        }
        if (this.data?.type === 'View') {
            this.form.get('agentWritable')?.disable({ emitEvent: false });
        }

        this._formService.applyTypeDependentValidators(
            this.form,
            this.form.get('type')?.value || ''
        );

        this.form
            .get('type')
            ?.valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((type: string) => {
                this._formService.applyTypeDependentValidators(this.form, type || '');
                this._applyTypeChangeLocks();
                this._changeDetectorRef.markForCheck();
            });

        this._applyTypeChangeLocks();
        this._changeDetectorRef.markForCheck();
    }

    private beginHydrationTask(): void {
        if (!this._isHydratingEditView) {
            return;
        }
        this._hydratePending++;
    }

    private endHydrationTask(): void {
        if (!this._isHydratingEditView) {
            return;
        }
        this._hydratePending--;
        if (this._hydratePending <= 0) {
            this._hydratePending = 0;
            this._isHydratingEditView = false;
            this.isLoadingDataSourceData = false;
            this._changeDetectorRef.markForCheck();
        }
    }

    get typeChangeLocks(): DataSourceTypeChangeLocks {
        return dataSourceTypeChangeLocks({
            dialogType: this.data?.type,
            sourceType: this.form?.get('type')?.value ?? this.data?.dataSource?.type,
            counts: this._contentCounts,
        });
    }

    private _applyTypeChangeLocks(): void {
        if (!this.form) {
            return;
        }
        const locks = this.typeChangeLocks;
        const typeCtrl = this.form.get('type');
        const externalCtrl = this.form.get('externalType');

        if (locks.lockCategoryType) {
            typeCtrl?.disable({ emitEvent: false });
        } else {
            typeCtrl?.enable({ emitEvent: false });
        }

        if (locks.lockExternalProvider) {
            externalCtrl?.disable({ emitEvent: false });
        } else if (this.data?.type !== 'View' && this.form.get('type')?.value === 'external') {
            externalCtrl?.enable({ emitEvent: false });
        }
        this._changeDetectorRef.markForCheck();
    }

    private _loadContentCountsForTypeLocks(hydrate: boolean): void {
        if (!hydrate) {
            return;
        }
        const ds = this.data?.dataSource;
        this._contentCounts = {
            tableCount: ds?.tableCount,
            uploadsCount: ds?.uploadsCount,
            fileCount: ds?.fileCount,
        };
        this._applyTypeChangeLocks();

        const eid = ds?.eid ?? ds?.id;
        if (eid == null || eid === '') {
            this.endHydrationTask();
            return;
        }

        this._dataSourceService
            .getById(eid)
            .pipe(
                takeUntil(this._unsubscribeAll),
                catchError(() => of(null)),
                finalize(() => this.endHydrationTask()),
            )
            .subscribe((source) => {
                if (!source) {
                    return;
                }
                this._contentCounts = {
                    tableCount: source.tableCount,
                    uploadsCount: source.uploadsCount,
                    fileCount: source.fileCount,
                };
                this._applyTypeChangeLocks();
            });
    }

    private _loadDepartments(hydrate = false): void {
        this._departmentService
            .getAll()
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    if (hydrate) {
                        this.endHydrationTask();
                    }
                }),
            )
            .subscribe({
                next: (res: Department[]) => {
                    this.departmenList = res ?? [];
                    this._applyTourPrefillIfNeeded();
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.departmenList = [];
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    private _maybeStartTourFormCoach(): void {
        if (this.data?.type !== 'Add' || !this._quickTour.isOnAddDataSourceStep()) {
            return;
        }
        this._quickTour.startDataSourceFormCoach();
    }

    private _applyTourPrefillIfNeeded(): void {
        if (
            this._tourPrefillApplied ||
            this.data?.type !== 'Add' ||
            !this._quickTour.isOnAddDataSourceStep()
        ) {
            return;
        }
        this._tourPrefillApplied = true;

        const firstDeptId = this.departmenList.find((d) => d.id != null)?.id;
        this.form.patchValue({
            name: TOUR_DEMO_DS_NAME,
            type: TOUR_DEMO_DS_TYPE,
            internalType: TOUR_DEMO_DS_INTERNAL_TYPE,
            department: firstDeptId != null ? [firstDeptId] : [],
        });
        this._formService.applyTypeDependentValidators(this.form, TOUR_DEMO_DS_TYPE);
        this._changeDetectorRef.markForCheck();
    }

    private _subscribeToDepartmentChanges(): void {
        this.form
            .get('department')
            ?.valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((departmentValue: number[] | number | string | null) => {
                const departmentIds =
                    this._formService.normalizeDepartmentIds(departmentValue);
                this._loadMembers(departmentIds);
                this._syncSharedDepartmentsWithAssignment(departmentIds);
            });
    }

    /** Keep share selection scoped to assigned departments; Add defaults new assignments to shared. */
    private _syncSharedDepartmentsWithAssignment(departmentIds: number[]): void {
        const assignedSet = new Set(departmentIds);
        const pruned = this.sharedDepartmentIds.filter(id => assignedSet.has(id));

        if (this.data?.type === 'Add') {
            const next = pruned.slice();
            departmentIds.forEach(id => {
                if (!next.includes(id)) {
                    next.push(id);
                }
            });
            this.sharedDepartmentIds = next;
            this._changeDetectorRef.markForCheck();
            return;
        }

        if (this.data?.type === 'Edit') {
            const previous = this.sharedDepartmentIds.slice().sort((a, b) => a - b);
            const next = pruned.slice().sort((a, b) => a - b);
            if (
                previous.length !== next.length ||
                previous.some((id, index) => id !== next[index])
            ) {
                this._applySharedDepartments(next);
                return;
            }
        }

        this.sharedDepartmentIds = pruned;
        this._changeDetectorRef.markForCheck();
    }

    private _assignedDepartmentIds(): number[] {
        return this._formService.normalizeDepartmentIds(
            this.form.get('department')?.value
        );
    }

    /** Departments eligible for sharing — only those assigned to this data source. */
    get shareableDepartments(): Department[] {
        const assigned = new Set(this._assignedDepartmentIds());
        if (!assigned.size) {
            return [];
        }
        return this.departmenList.filter(
            department => department.id != null && assigned.has(+department.id)
        );
    }

    trackByFn(_index: number, department: Department): string | number {
        return department.id ?? _index;
    }

    get sharedDepartmentsCount(): number {
        const assigned = new Set(this._assignedDepartmentIds());
        return this.sharedDepartmentIds.filter(id => assigned.has(id)).length;
    }

    get assignedDepartmentIds(): number[] {
        return this._assignedDepartmentIds();
    }

    get shareDepartmentsTotal(): number {
        return this.shareableDepartments.length;
    }

    isDepartmentShared(departmentId: number | string | undefined): boolean {
        if (departmentId == null) {
            return false;
        }
        const id = +departmentId;
        return Number.isFinite(id) && this.sharedDepartmentIds.includes(id);
    }

    onDepartmentShareToggle(
        departmentId: number | string | undefined,
        event: MatSlideToggleChange
    ): void {
        if (departmentId == null) {
            return;
        }
        const id = +departmentId;
        if (!Number.isFinite(id)) {
            return;
        }

        let next = this.sharedDepartmentIds.slice();
        if (event.checked) {
            if (!next.includes(id)) {
                next.push(id);
            }
        } else {
            next = next.filter(existing => existing !== id);
        }
        this._applySharedDepartments(next);
    }

    private _applySharedDepartments(next: number[]): void {
        const assignedSet = new Set(this._assignedDepartmentIds());
        const normalized = [...new Set(
            next.filter(id => Number.isFinite(id) && assignedSet.has(id))
        )].sort((a, b) => a - b);
        const dataSourceId = this.data?.dataSource?.eid;

        if (!dataSourceId) {
            this.sharedDepartmentIds = normalized;
            this._changeDetectorRef.markForCheck();
            return;
        }

        if (this.data?.type === 'View') {
            return;
        }

        const previous = this.sharedDepartmentIds.slice().sort((a, b) => a - b);
        if (
            previous.length === normalized.length &&
            previous.every((id, index) => id === normalized[index])
        ) {
            return;
        }

        this.sharedDepartmentIds = normalized;
        this.shareSaving = true;
        this._dataSourceService
            .shareWithDepartments(dataSourceId, normalized)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.shareSaving = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    if (this.data?.dataSource) {
                        this.data.dataSource.sharedDepartmentIds = normalized;
                        this.data.dataSource.isPublic = normalized.length > 0;
                    }
                    this._alertService.successMessage(
                        this._translocoService.translate(
                            'Department sharing updated successfully.'
                        )
                    );
                },
                error: (error) => {
                    this.sharedDepartmentIds = previous;
                    this._changeDetectorRef.markForCheck();
                    this._alertService.failedMessage(
                        error?.error?.message ??
                            this._translocoService.translate(
                                'Failed to update department sharing.'
                            )
                    );
                },
            });
    }

    compareManagers(a: any, b: any): boolean {
        if (a === b) {
            return true;
        }
        if (
            a != null &&
            b != null &&
            typeof a === 'object' &&
            typeof b === 'object'
        ) {
            return +a.id === +b.id;
        }
        return false;
    }

    private _subscribeToLoggedInUser(): void {
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: unknown) => {
                const raw = user as Record<string, unknown> | null;
                const nestedData =
                    raw && typeof raw['data'] === 'object' && raw['data'] !== null
                        ? (raw['data'] as Record<string, unknown>)
                        : null;
                const nestedUser =
                    nestedData &&
                    typeof nestedData['user'] === 'object' &&
                    nestedData['user'] !== null
                        ? (nestedData['user'] as Record<string, unknown>)
                        : null;
                this._currentUser = nestedUser ?? nestedData ?? raw ?? null;
                const id = Number(this._currentUser?.['id']);
                if (Number.isFinite(id) && id > 0) {
                    this._loggedInUserId = id;
                }
                const email = String(this._currentUser?.['email'] ?? '')
                    .trim()
                    .toLowerCase();
                if (email) {
                    this._loggedInUserEmail = email;
                }
                if (this.isAdmin) {
                    this._loggedInDisplayName =
                        this._translocoService.translate('Admin');
                } else {
                    const displayName = this._resolveLoggedInDisplayName(
                        this._currentUser ?? {},
                    );
                    if (displayName) {
                        this._loggedInDisplayName = displayName;
                    }
                }
                if (this.data?.type === 'Add') {
                    this._seedLoggedInOwnerForAdd();
                }
            });

        this._userService.memberStaffProfile$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((profile) => {
                const name = profile?.name?.trim();
                if (name && !this.isAdmin) {
                    this._loggedInDisplayName = name;
                }
                if (profile?.id != null && Number.isFinite(Number(profile.id))) {
                    this._loggedInUserId = Number(profile.id);
                }
                if (profile?.email) {
                    this._loggedInUserEmail = String(profile.email)
                        .trim()
                        .toLowerCase();
                }
                if (this.data?.type === 'Add') {
                    this._seedLoggedInOwnerForAdd();
                }
            });
    }

    private _resolveLoggedInDisplayName(user: Record<string, unknown>): string {
        const candidates = [
            user['user_name'],
            user['acc_name'],
            user['name'],
            user['company_name'],
            user['username'],
            user['email'],
        ];
        for (const candidate of candidates) {
            if (candidate != null && String(candidate).trim() !== '') {
                return String(candidate).trim();
            }
        }
        return '';
    }

    private _extractOwnerRole(source: Record<string, unknown> | null): string {
        if (!source) {
            return '';
        }
        for (const key of ['role', 'userRole', 'ownerRole']) {
            const value = source[key];
            if (value != null && String(value).trim() !== '') {
                return this._formService.normalizeOwnershipRole(String(value).trim());
            }
        }
        return this._formService.normalizeOwnershipRole(this._authService.userRole);
    }

    private _buildLoggedInOwnerOption(): {
        id: number;
        name: string;
        role: string;
        email: string;
    } | null {
        const id = this._loggedInUserId;
        if (id == null || !Number.isFinite(id) || id <= 0) {
            return null;
        }
        const role =
            this._extractOwnerRole(this._currentUser) ||
            this._formService.normalizeOwnershipRole(this._authService.userRole);
        const name = this.isAdmin
            ? this._translocoService.translate('Admin')
            : this._loggedInDisplayName ||
              this._resolveLoggedInDisplayName(this._currentUser ?? {}) ||
              this._loggedInUserEmail ||
              String(id);
        return {
            id,
            name,
            role,
            email: this._loggedInUserEmail,
        };
    }

    private _managerValueFromMember(member: {
        id: number;
        role?: string;
        userRole?: string;
    }): { id: number; role: string } {
        return {
            id: +member.id,
            role: this._formService.normalizeOwnershipRole(
                member.role ?? member.userRole ?? '',
            ),
        };
    }

    private _setManagerValue(member: {
        id: number;
        role?: string;
        userRole?: string;
    }): void {
        this.form.get('manager')?.setValue(this._managerValueFromMember(member), {
            emitEvent: false,
        });
    }

    /** Put the signed-in user into membersList and select them in Owned By (Add). */
    private _ensureLoggedInOwnerSelectedForAdd(): void {
        if (this.data?.type !== 'Add' || !this.form) {
            return;
        }
        const option = this._buildLoggedInOwnerOption();
        if (!option) {
            return;
        }
        const existingIdx = this.membersList.findIndex(m => +m.id === option.id);
        if (existingIdx >= 0) {
            this.membersList = this.membersList.map((m, i) =>
                i === existingIdx
                    ? {
                          ...m,
                          name: option.name,
                          role: option.role || m.role,
                          email: option.email || m.email,
                      }
                    : m,
            );
        } else {
            this.membersList = [option, ...this.membersList];
        }
        this._setManagerValue(option);
        this._changeDetectorRef.markForCheck();
    }

    /** Put the signed-in user into membersList and select them in Owned By (Add, no department yet). */
    private _seedLoggedInOwnerForAdd(): void {
        if (this.data?.type !== 'Add' || !this.form) {
            return;
        }
        if (this._assignedDepartmentIds().length) {
            return;
        }
        this._ensureLoggedInOwnerSelectedForAdd();
    }

    /** Org admin row in the Owned By list (`company` / `user` / `admin` roles). */
    private _findOrgAdminInMembersList(): {
        id: number;
        role?: string;
        userRole?: string;
    } | null {
        const match = this.membersList.find((member) => {
            const role = String(member?.role || member?.userRole || '')
                .toLowerCase()
                .trim();
            return role === 'company' || role === 'user' || role === 'admin';
        });
        if (match?.id == null) {
            return null;
        }
        return match;
    }

    /** Org-admin profile name is often the company name — show "Admin" in Owned By. */
    private _normalizeOrgAdminDisplayNames(): void {
        const adminLabel = this._translocoService.translate('Admin');
        this.membersList = this.membersList.map((member) => {
            const role = String(member?.role || member?.userRole || '')
                .toLowerCase()
                .trim();
            if (role === 'company' || role === 'user' || role === 'admin') {
                return { ...member, name: adminLabel };
            }
            return member;
        });
    }

    private _currentManagerId(): number {
        const current = this.form.get('manager')?.value;
        if (current != null && typeof current === 'object') {
            return +current.id;
        }
        if (current !== '' && current != null) {
            return +current;
        }
        return NaN;
    }

    /**
     * Add mode: default Owned By to the logged-in user (Admin label for org admin).
     * Keep the current selection when it is still in the department-scoped list;
     * otherwise prefer the logged-in user, then org Admin.
     */
    private _applyDefaultManagerForAdd(): void {
        if (this.data?.type !== 'Add') {
            return;
        }

        const managerCtrl = this.form.get('manager');
        if (!managerCtrl) {
            return;
        }

        const currentId = this._currentManagerId();
        if (
            Number.isFinite(currentId) &&
            this.membersList.some(member => +member.id === currentId)
        ) {
            return;
        }

        const loggedIn = this._buildLoggedInOwnerOption();
        if (
            loggedIn &&
            this.membersList.some(member => +member.id === loggedIn.id)
        ) {
            this._setManagerValue(loggedIn);
            return;
        }

        const admin = this._findOrgAdminInMembersList();
        if (admin) {
            this._setManagerValue(admin);
            return;
        }

        if (loggedIn) {
            this._ensureLoggedInOwnerSelectedForAdd();
            return;
        }

        managerCtrl.setValue('', { emitEvent: false });
    }

    private _syncOwnedByEnabledForAdd(hasDepartments: boolean): void {
        if (this.data?.type !== 'Add') {
            return;
        }
        const managerCtrl = this.form.get('manager');
        if (hasDepartments) {
            managerCtrl?.enable({ emitEvent: false });
        } else {
            managerCtrl?.disable({ emitEvent: false });
        }
    }

    private _loadMembers(departmentIds: number[], hydrate = false): void {
        if (!departmentIds?.length) {
            if (this.data?.type === 'Add') {
                this.membersList = [];
                this._syncOwnedByEnabledForAdd(false);
                this._seedLoggedInOwnerForAdd();
            } else {
                this.membersList = [];
                this.form.get('manager')?.setValue('', { emitEvent: false });
            }
            this._changeDetectorRef.markForCheck();
            if (hydrate) {
                this.endHydrationTask();
            }
            return;
        }

        if (this.data?.type === 'Add') {
            this._syncOwnedByEnabledForAdd(true);
            const current = this.form.get('manager')?.value;
            if (current === null || current === undefined || current === '') {
                this._ensureLoggedInOwnerSelectedForAdd();
            }
        }

        const url = `${environment.apiLink}users/get-organization-users`;
        let params = new HttpParams().set('shouldSelfInclude', 'true');
        departmentIds.forEach((departmentId) => {
            params = params.append('departmentIds', departmentId.toString());
        });

        this._httpClient
            .get(url, { params })
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    if (hydrate) {
                        this.endHydrationTask();
                    }
                }),
            )
            .subscribe({
                next: (response: any) => {
                    const members = response?.data?.users ?? response?.data?.members ?? [];
                    if (
                        response &&
                        response.success &&
                        Array.isArray(members)
                    ) {
                        this.membersList = members;
                        this._normalizeOrgAdminDisplayNames();

                        if (this.data?.type === 'Add') {
                            this._applyDefaultManagerForAdd();
                        } else {
                            const selectedId = this._currentManagerId();
                            if (
                                Number.isFinite(selectedId) &&
                                !this.membersList.some(
                                    member => +member.id === selectedId
                                )
                            ) {
                                this.form
                                    .get('manager')
                                    ?.setValue('', { emitEvent: false });
                            }
                        }
                    } else {
                        console.error('Invalid members response structure');
                        this.membersList = [];
                        if (this.data?.type === 'Add') {
                            this._ensureLoggedInOwnerSelectedForAdd();
                        }
                    }
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    console.error('Error fetching members:', error);
                    this.membersList = [];
                    if (this.data?.type === 'Add') {
                        this._ensureLoggedInOwnerSelectedForAdd();
                    }
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    close(): void {
        this._dialogRef.close(false);
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const isEdit = this.data?.type === 'Edit';
        const includeOwnership =
            this.data?.type === 'Add' || this.data?.type === 'Edit';
        const payload = this._formService.buildPayload(
            this.form,
            this.membersList,
            includeOwnership
        );

        if (isEdit) {
            if (this._departmentsChangedFromInitial()) {
                const dialogRef = this._dialog.open(DeleteConfirmationComponent, {
                    panelClass: 'cropperDialog',
                    data: this._translocoService.translate(
                        'Changing departments may reassign ownership of connected agents to Admin when the current owner no longer belongs to the new departments. Continue?'
                    ),
                });
                dialogRef
                    .afterClosed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((confirmed: boolean) => {
                        if (confirmed) {
                            this._updateDataSource(payload);
                        }
                    });
                return;
            }
            this._updateDataSource(payload);
            return;
        }

        this._dataSourceService
            .create(payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: {
                    data?: { source?: { eid?: string | number } };
                }) => {
                    const newEid = res?.data?.source?.eid;
                    const shareIds = this.sharedDepartmentIds.slice();
                    if (newEid && shareIds.length) {
                        this._shareNewDataSource(newEid, shareIds);
                    } else {
                        this._finishSave(false);
                    }
                },
                error: error =>
                    this._alertService.failedMessage(
                        error?.error?.message || 'Something went wrong.'
                    ),
            });
    }

    private _departmentsChangedFromInitial(): boolean {
        const next = this._formService
            .normalizeDepartmentIds(this.form.get('department')?.value)
            .slice()
            .sort((a, b) => a - b);
        if (next.length !== this._initialDepartmentIds.length) {
            return true;
        }
        return next.some((id, i) => id !== this._initialDepartmentIds[i]);
    }

    private _updateDataSource(payload: unknown): void {
        this._dataSourceService
            .update(payload, this.data.dataSource.eid)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => this._finishSave(true),
                error: error =>
                    this._alertService.failedMessage(
                        error?.error?.message || 'Something went wrong.'
                    ),
            });
    }

    /** Share a just-created data source; the source is already created, so a share
     *  failure is a non-fatal warning rather than a failed creation. */
    private _shareNewDataSource(
        eid: string | number,
        departmentIds: number[]
    ): void {
        this._dataSourceService
            .shareWithDepartments(eid, departmentIds)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => this._finishSave(false),
                error: () => {
                    this._alertService.successMessage(
                        this._translocoService.translate(
                            'Data source created. Sharing with departments failed; you can retry from Edit.'
                        )
                    );
                    this._dialogRef.close(true);
                },
            });
    }

    private _finishSave(isEdit: boolean): void {
        this._alertService.successMessage(
            `DataSource has been ${isEdit ? 'updated' : 'created'} successfully!!`
        );
        this._dialogRef.close(true);
    }

    createJoin(): FormControl {
        return this._formService.createJoinControl();
    }

    get joins(): FormArray {
        return this.form.get('joins') as FormArray;
    }

    addJoin(): void {
        this.joins.insert(0, this.createJoin());
    }

    trackByJoinIndex(index: number): number {
        return index;
    }

    removeJoin(index: number): void {
        this.joins.removeAt(index);
    }

    get canGenerateDescription(): boolean {
        return (
            this.data?.type === 'Edit' &&
            !!this.data?.dataSource?.eid
        );
    }

    get canShareWithDepartments(): boolean {
        if (this.data?.type === 'Add') {
            return true;
        }
        return (
            (this.data?.type === 'Edit' || this.data?.type === 'View') &&
            !!this.data?.dataSource?.eid
        );
    }

    generateDescription(): void {
        if (!this.canGenerateDescription) {
            return;
        }

        const dataSourceId = this.data.dataSource.eid;

        this._dataSourceService
            .generateDatasourceDescription(dataSourceId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: any) => {
                    const generatedText =
                        res?.data?.description || '';

                    if (!generatedText) {
                        this._alertService.failedMessage(
                            'No description was generated.'
                        );
                        return;
                    }

                    this.form
                        .get('description')
                        ?.setValue(generatedText);

                    this.form
                        .get('description')
                        ?.markAsDirty();

                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this._alertService.failedMessage(
                        'Failed to generate description.'
                    );
                },
            });
    }


}
